import React, { ReactNode, useState } from "react";
import {
  Accordion,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Spacer,
  Stack,
  Tag,
  TagLabel,
  Text,
} from "@chakra-ui/react";
import { timeAgo } from "@keita/lanepatrol-sdk";
import { observer } from "mobx-react";
import ReactMarkdown from "react-markdown";
import { t } from "i18next";
import { CommentEditor } from "./inputs/CommentEditor";
import { CommentCreator } from "./inputs/CommentCreator";
import { OsaComment } from "../models/OsaComment";
import { useStores } from "../../shared/stores/RootStore";
import { toast } from "../../shared/views/CustomToaster";

interface Props {
  comment: OsaComment;
  forceResolved?: boolean;
  replyToThread?: () => void;
}
const CommentBase: React.FC<Props> = ({
  comment,
  forceResolved,
  replyToThread,
}) => {
  const { osaCommentStore } = useStores();
  const [editMode, setEditMode] = useState(false);
  const [replyMode, setReplyMode] = useState(false);

  const isMainThread = !comment.mainThreadId;
  const resolved = forceResolved ?? comment.resolved;

  const toggleResolved = () => {
    if (comment.resolved) {
      osaCommentStore.unresolveComment(comment.id);
    } else {
      osaCommentStore.resolveComment(comment.id);
    }
  };

  const deleteComment = () => {
    if (window.confirm(t("map:comments.deleteConfirm"))) {
      osaCommentStore
        .deleteComment(comment)
        .then(() => {
          toast("success", t("map:comments.deleteSuccess"));
        })
        .catch(() => {
          toast("error", t("map:comments.deleteError"));
        });
    }
  };

  return (
    <MaybeAccordion enabled={isMainThread}>
      <HStack py={2} alignItems="start">
        <Stack minW="24px">
          <Avatar.Root size="xs">
            <AvatarFallback name={comment.writerEmail} />
            <AvatarImage />
          </Avatar.Root>
          {isMainThread && (
            <Accordion.ItemTrigger
              boxSize={6}
              p={0}
              borderRadius="100%"
              justifyContent="center"
            >
              <Accordion.ItemIndicator />
            </Accordion.ItemTrigger>
          )}
        </Stack>
        <Stack flexGrow={1}>
          <Stack fontSize="xs" gap={1}>
            <Flex>
              <Box fontSize="xs" overflow="hidden" wordWrap="normal">
                <Text as="span" fontWeight="semibold" mr={2}>
                  {comment.writerEmail}
                </Text>
                <Text color="gray.500" as="span">
                  {timeAgo(new Date(comment.createdAt!))}
                </Text>
              </Box>
              <Spacer />
              {comment.resolved && (
                <Tag.Root size="sm">
                  <TagLabel>Resolved</TagLabel>
                </Tag.Root>
              )}
            </Flex>

            {editMode ? (
              <CommentEditor
                onClose={() => setEditMode(false)}
                comment={comment}
              />
            ) : (
              <ReactMarkdown>
                {comment.edited
                  ? `${comment.content} *(Edited)*`
                  : comment.content}
              </ReactMarkdown>
            )}
          </Stack>
          <MaybeAccordionPanel enabled={isMainThread}>
            <Stack fontSize="xs" gap={1}>
              <HStack mb={2}>
                <ButtonGroup
                  size="xs"
                  attached
                  variant="outline"
                  colorPalette="primary"
                >
                  {!resolved && (
                    <Button
                      disabled={editMode}
                      onClick={replyToThread || (() => setReplyMode(true))}
                    >
                      {t(`map:comments.reply`)}
                    </Button>
                  )}

                  {isMainThread && (
                    <Button disabled={editMode} onClick={toggleResolved}>
                      {resolved
                        ? t(`map:comments.unresolve`)
                        : t(`map:comments.resolve`)}
                    </Button>
                  )}

                  {comment.writtenByMe && !resolved && (
                    <Button
                      disabled={editMode}
                      onClick={() => setEditMode(true)}
                    >
                      {t(`map:comments.edit`)}
                    </Button>
                  )}

                  {comment.writtenByMe && !resolved && (
                    <Button disabled={editMode} onClick={deleteComment}>
                      {t(`map:comments.delete`)}
                    </Button>
                  )}
                </ButtonGroup>
              </HStack>

              {comment.replies.map((reply, i) => (
                <Comment
                  key={i}
                  comment={reply}
                  forceResolved={resolved}
                  replyToThread={() => setReplyMode(true)}
                />
              ))}

              {replyMode && (
                <CommentCreator
                  onClose={() => setReplyMode(false)}
                  osaWayId={comment.osaWayId}
                  mainThread={comment}
                  nodeId={comment.osaNodeId || undefined}
                />
              )}
            </Stack>
          </MaybeAccordionPanel>
        </Stack>
      </HStack>
    </MaybeAccordion>
  );
};

export const Comment = observer(CommentBase);

interface MaybeAccordionProps {
  enabled?: boolean;
  children?: ReactNode;
}
const MaybeAccordion: React.FC<MaybeAccordionProps> = ({
  enabled = false,
  children,
}) => {
  if (!enabled) return <>{children}</>;
  return (
    <Accordion.Root collapsible defaultValue={["0"]}>
      <Accordion.Item value={"0"}>{children}</Accordion.Item>
    </Accordion.Root>
  );
};

const MaybeAccordionPanel: React.FC<MaybeAccordionProps> = ({
  enabled = false,
  children,
}) => {
  if (!enabled) return <>{children}</>;
  return <Accordion.ItemContent p={0}>{children}</Accordion.ItemContent>;
};
